import { SubmitHandler, useForm, type Control } from 'react-hook-form';
import SubmitButton from './SubmitButton';
import Grid from '@mui/material/Grid';
import BirthdayTextInput from './FormElements/BirthdayTextInput';
import SexInput from './FormElements/SexInput';
import useTransition from '../hooks/useTransition';
import type { IPolarisInterviewQuestion } from 'src/packages/polaris/types';
import useIsPolaris from '../hooks/useIsPolaris';
import TextFormInput from './FormElements/TextFormInput';
import usePolarisActor from 'src/hooks/usePolarisActor';

export interface IIdentifiersForm {
  firstName: string;
  lastName: string;
  dob: string;
  sex?: 'm' | 'f';
}

export const IdentifiersInputFields = ({
  control,
  required,
  enabledFields = ['firstName', 'lastName', 'dob', 'sex'],
  hideTooltip,
}: {
  control: Control<any, any>;
  required?: boolean;
  enabledFields?: string[];
  hideTooltip?: boolean;
}) => {
  return (
    <Grid container direction="column" spacing={2}>
      {enabledFields.includes('firstName') && (
        <Grid item>
          <TextFormInput
            autoComplete="given-name"
            control={control}
            name="firstName"
            label={`Name${required ? '*' : ''}`}
            placeholder="First name"
            errorMessage="Enter your first name"
            required={required}
          />
        </Grid>
      )}
      {enabledFields.includes('lastName') && (
        <Grid item>
          <TextFormInput
            autoComplete="family-name"
            control={control}
            name="lastName"
            placeholder="Last name"
            errorMessage="Enter your last name"
            required={required}
          />
        </Grid>
      )}
      {enabledFields.includes('dob') && (
        <Grid item mt={1}>
          <BirthdayTextInput
            control={control}
            name="dob"
            label={`Date of birth${required ? '*' : ''}`}
            required={required}
            hideTooltip={hideTooltip}
          />
        </Grid>
      )}
      {enabledFields.includes('sex') && (
        <Grid item mt={1}>
          <SexInput
            autoComplete="sex"
            control={control}
            name="sex"
            label={`Sex at birth${required ? '*' : ''}`}
            required={required}
          />
        </Grid>
      )}
    </Grid>
  );
};

const IdentifiersForm = ({
  question,
  onQuestionSubmit,
}: {
  question: IPolarisInterviewQuestion;
  onQuestionSubmit?: (index: number) => void;
}) => {
  const transition = useTransition();
  const isPolaris = useIsPolaris();
  const state = usePolarisActor()?.getSnapshot();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<IIdentifiersForm>({
    defaultValues: {
      firstName: state?.context?.identifiers?.firstName || '',
      lastName: state?.context?.identifiers?.lastName || '',
      dob: state?.context?.identifiers?.dob || '',
      sex: state?.context?.identifiers?.sex || undefined,
    },
  });

  /** This submit handler only fires when the data is valid */
  const onSubmit: SubmitHandler<IIdentifiersForm> = (data) => {
    onQuestionSubmit?.(question.index);

    transition({
      data: {
        identifiers: data,
        question: {
          ...question,
          key: 'identifiers',
        },
        isPolaris,
      },
    });
  };

  return (
    <>
      <IdentifiersInputFields control={control} />
      <SubmitButton onClick={handleSubmit(onSubmit)} text={isDirty ? 'Submit' : 'Skip'} />
    </>
  );
};

export default IdentifiersForm;
